import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { Grid, List, TrendingUp, MapPin, DollarSign } from 'lucide-react'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

interface OECDData {
  gdp: number
  gdpGrowth: number
  tradeBalance: number
  exports: number
  imports: number
  inflation: number
  unemployment: number
}

interface Market {
  id: string
  country: string
  countryCode?: string
  region: string
  opportunityScore: number
  growthRate: number
  marketSize: string
  averageOrderValue: number
  competition: 'Low' | 'Medium' | 'High'
  requirements: string[]
  oecd?: OECDData
}

const fetchMarkets = async (filters: any) => {
  const { data } = await axios.get(`${API_BASE_URL}/markets`, { params: filters })
  return data
}

export const MarketsPage: React.FC = () => {
  useParams() // keep URL params handling
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [filters, setFilters] = useState({
    region: 'all',
    minScore: 0,
    sortBy: 'score',
  })
  const [page, setPage] = useState(1)
  const pageSize = 12

  const { data, isLoading } = useQuery({
    queryKey: ['markets', filters, page],
    queryFn: () => fetchMarkets({ ...filters, page, pageSize }),
  })

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value })
    setPage(1)
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#50C878'
    if (score >= 60) return '#4A90E2'
    if (score >= 40) return '#FF6B35'
    return '#999'
  }

  const markets: Market[] = data?.markets || []
  const totalPages = data?.totalPages || 1

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      <Header />

      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>Market Opportunities</h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Explore and analyze expansion opportunities across {markets.length} markets
          </p>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Region
                </label>
                <select
                  value={filters.region}
                  onChange={(e) => handleFilterChange('region', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="all">All Regions</option>
                  <option value="europe">Europe</option>
                  <option value="asia">Asia</option>
                  <option value="americas">Americas</option>
                  <option value="oceania">Oceania</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Min. Score
                </label>
                <select
                  value={filters.minScore}
                  onChange={(e) => handleFilterChange('minScore', parseInt(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="0">All</option>
                  <option value="50">50+</option>
                  <option value="70">70+</option>
                  <option value="80">80+</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Sort By
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <option value="score">Opportunity Score</option>
                  <option value="growth">Growth Rate</option>
                  <option value="size">Market Size</option>
                  <option value="competition">Competition</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List />
              </Button>
            </div>
          </div>
        </Card>

        {/* Markets Grid/List */}
        {isLoading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
            gap: '24px',
          }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                style={{
                  backgroundColor: '#FFF',
                  borderRadius: '12px',
                  height: '250px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
              gap: '24px',
              marginBottom: '32px',
            }}>
              {markets.map((market) => (
                <Card
                  key={market.id}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                  onClick={() => navigate(`/markets/${market.country}`)}
                  onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '20px', color: '#1A1A1A' }}>{market.country}</h3>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '24px',
                        backgroundColor: `${getScoreColor(market.opportunityScore)}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: getScoreColor(market.opportunityScore),
                      }}
                    >
                      {market.opportunityScore}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MapPin size={16} style={{ color: '#666' }} />
                      <span style={{ fontSize: '14px', color: '#666' }}>{market.region}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TrendingUp size={16} style={{ color: '#50C878' }} />
                      <span style={{ fontSize: '14px', color: '#1A1A1A' }}>
                        {market.growthRate}% growth
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DollarSign size={16} style={{ color: '#4A90E2' }} />
                      <span style={{ fontSize: '14px', color: '#1A1A1A' }}>
                        {market.marketSize} market
                      </span>
                    </div>

                    {/* OECD Economic Data */}
                    {market.oecd && (
                      <div style={{
                        backgroundColor: '#F0F7FF',
                        borderRadius: '6px',
                        padding: '10px',
                        marginTop: '8px',
                      }}>
                        <div style={{ fontSize: '11px', color: '#4A90E2', fontWeight: 600, marginBottom: '6px' }}>
                          OECD Economic Indicators
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '11px' }}>
                          <span style={{ color: '#666' }}>GDP Growth: <strong style={{ color: market.oecd.gdpGrowth > 2 ? '#50C878' : '#FF6B35' }}>{market.oecd.gdpGrowth}%</strong></span>
                          <span style={{ color: '#666' }}>Inflation: <strong>{market.oecd.inflation}%</strong></span>
                          <span style={{ color: '#666' }}>Trade: <strong style={{ color: market.oecd.tradeBalance > 0 ? '#50C878' : '#FF6B35' }}>${market.oecd.tradeBalance}B</strong></span>
                          <span style={{ color: '#666' }}>Unemployment: <strong>{market.oecd.unemployment}%</strong></span>
                        </div>
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px',
                      paddingTop: '12px',
                      borderTop: '1px solid #E0E0E0',
                    }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        Competition: <strong>{market.competition}</strong>
                      </span>
                      <span style={{ fontSize: '12px', color: '#4A90E2' }}>
                        View Details →
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'primary' : 'outline'}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
